
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import {
  UpgradeStateEnum, creditTaskStatusTypeEnum, creditTaskSuspendSceneEnum, galileoTypeEnum,
} from '@/enums/autarkyUpgrade';
import type { AdjustQuotaType } from '@/dict/quotaIncrease-type';
import { PreCreditUserSourceEnums, NormalPreCreditModelEnums } from '@/enums/home-type';
import { CouponTypeEnum } from '@/api/coupon/types';

export interface CreditDataType extends UserLoanType {
  /** 激活进度：如 30% 60%,非极简(0,40,70,90,100,默认0)，极简(0,90,100,默认0) */
  activationProgress: number;
  /** 授信额度 */
  creditAmount: number | string;
  /** 是否有授信成功，冻结 逾期 或者自营 B 升级成功的记录【true:有 false:无】 */
  creditStatus: boolean;
  /** 用户资料填写状态：1、用户资料未填写(无任何信息) 2、用户资料填写中 3、用户资料填写完成 */
  fillInTheInformation: UserInfoStatus;
  /** 判断用户是否第一次进行授信 */
  hasEnter: boolean;
  /** 是否有进行中的任务 */
  progressingTaskId: string;
  /** 今日应还订单数 */
  shouldPayNum: number | string;
  /** 激活步数: 1、用户基础信息完成 2、用户联系人信息完成 3、用户身份证正反面上传完成 4、活体完成 */
  stepCount: number;
  /** 是否需要学历字段 */
  needEducation: boolean;
  /** 页面显示版本 */
  displayVersion: string;
  /** 银河授信总金额 */
  creditAmountTotal: string;
  /** 银河存量用户弹窗引导 */
  appletYinhePop: boolean;
  /** 用户是否需要补充月薪 */
  needMonthSalary: boolean;
  /** 用户是否需要更新紧急联系人信息 --紧急联系人信息不符合要求的场景  true 需要更新，false 无需更新，默认 false */
  needEmergencyContact: boolean;
  /** 是否存量用户 true:是，false:否 默认false */
  isStock: boolean;
  /** 存量用户预计提额后剩余授信金额，单位元 */
  creditNewAmount?: string;
  /** 额度调整 */
  adjustQuota?: AdjustQuotaType;
  /** 流量分发前资料是否填写完整 */
  firstInfoComplete?: boolean;
  /** 授信任务状态(0初始化、1执行中、2结束、3超时、4压单、5挂起) */
  creditTaskStatus?: creditTaskStatusTypeEnum;
  /** 授信任务挂起场景 */
  suspendScene?: creditTaskSuspendSceneEnum;
  /** 流量分发前 用户资料填写状态：1、用户资料未填写 2、用户资料填写中 3、用户资料填写完成 */
  firstInfoFillInTheInformation?: UserInfoStatus;
  /** 身份证有效期是否小于30天(true: 小于;false: 不小于) */
  identityExpiresFor30: boolean;
  /** 是否预授信用户 */
  preCredit: boolean;
  /** 预授信用户来源 */
  preCreditUserSource: PreCreditUserSourceEnums;
  /** 普通预授信模式 */
  normalPreCreditModel: NormalPreCreditModelEnums;
  /** 优惠券类型 */
  freeCouponType?: CouponTypeEnum;
  /** 优惠券名称-金额部分 */
  couponNameMoney?: string;
  /** 优惠券名称-时间部分 */
  couponNameDay?: string;
}


export interface UserInfoProgress {
  /** 用户资料填写状态：1、用户资料未填写 2、用户资料填写中 3、用户资料填写完成 */
  fillInTheInformation: UserInfoStatus;
  /** 激活进度：如 30% 60%,非极简(0,40,70,90,100,默认0)，极简(0,90,100,默认0) */
  activationProgress: number;
  /** 激活步数: 1、用户基础信息完成 2、用户联系人信息完成 3、用户身份证正反面上传完成 4、活体完成 */
  stepCount: number;
  /** 用户是否需要补充学历信息 */
  needEducation: boolean;
  /** 用户是否需要补充月薪 */
  needMonthSalary: boolean;
  /** 用户是否需要更新紧急联系人信息 --紧急联系人信息不符合要求的场景  true 需要更新，false 无需更新，默认 false */
  needEmergencyContact: boolean;
  /** 流量分发前资料是否填写完整 */
  firstInfoComplete?: boolean;
  /** 流量分发前 用户资料填写状态：1、用户资料未填写 2、用户资料填写中 3、用户资料填写完成 */
  firstInfoFillInTheInformation?: UserInfoStatus;
  /** 身份证有效期是否小于30天(true: 小于;false: 不小于) */
  identityExpiresFor30: boolean;
}

export interface UserCreditType {
  /** 分发url，供客户端使用 */
  link: string;
  businessData: UserCreditOriginType;
}

export interface UserCreditOriginType {
  /** 授信剩余金额 */
  creditAmount: string,
  /** 授信总金额--前端无使用场景 */
  creditAmountTotal: string,
  /** 是否有授信的记录【true:有 false:无】 */
  creditStatus: boolean,
  /** 是否有进行中的任务 -- 是否有在借款中的 无'' 有就去对应taskId */
  progressingTaskId: string,
  /** 判断用户是否第一次进行授信 */
  hasEnter: boolean,
  /** 授信任务状态(0初始化、1执行中、2结束、3超时、4压单、5挂起) */
  creditTaskStatus?: creditTaskStatusTypeEnum,
  /** 授信任务挂起场景 */
  suspendScene?: creditTaskSuspendSceneEnum;
  /** 是否预授信用户 */
  preCredit: boolean,
  /** 预授信用户来源 */
  preCreditUserSource: PreCreditUserSourceEnums,
  /** 普通预授信模式 */
  normalPreCreditModel: NormalPreCreditModelEnums;
}

/** 零用钱订单状态 */
export enum PocketMoneyStatusEnum {
  /** 提现成功 */
  LENT = 'LENT',
  /** 提现失败 */
  LEND_FAILED = 'LEND_FAILED',
}

export interface UserLoanType {
  /** 是否有借款成功的订单【true:有 false:无】 */
  hasOrder: boolean,
  /** 今日逾期订单数量 */
  overdueNum: number,
  /** 待还款订单数量 */
  needRepayOrderNum: number,
  /** 按期还款订单数 */
  onTimeRepaymentOrdersCount?: number,
  /** 七日内待还款订单数 */
  sevenDayRepaymentOrdersCount?: number,
  /** 还款计划详情（只有首页需要展示具体还款卡片时才返回，前端判断时依赖此字段，此字段优先级最高） */
  planDetail?: {
    /** 还款日期：2025-07-16 */
    repaymentDate:string,
    /** 还款金额：1228.23 */
    repaymentAmount:string,
    /** 是否逾期 */
    isOverdue: boolean,
    /** 订单号 */
    loanOrderId:string,
    /** 资方code */
    capitalCode?:string,
    /** 渠道id */
    commodityId:string,
    /** 还款期数，不存在期数则为提前结清，缩期合并还款只需传递第一期期数信息 */
    period: number,
  },
  /** 页面显示版本(ZHNEYOUQIAN,ALI_APPLET_REJECT) - 判断是否需要过渡到银河项目 */
  displayVersion: string,
  /** 用户额度调整 */
  adjustQuota?: AdjustQuotaType,
  /** 零用钱状态 */
  pocketMoneyStatus?: PocketMoneyStatusEnum,
  /** 零用钱可用额度 */
  pocketMoneyAvailableAmount?: string,
  /** 还款逾期实际天数(逾期弹窗) */
  overdueDays?: number,
   /** 还款逾期实际天数(还款入口-提示气泡) */
  moduleOverdueDays?: number,
}

export interface AliProductStatus {
  // 商品Id
  productId?: string;
  // 今日限量是否已满
  isLimitToday?: boolean;
  // 是否暂停
  isSuspend?: boolean;
  // 恢复时间
  resumeTime?: string;
  // 是否长期停止
  isStop?: boolean;
}

// 查询用户是否存在当期待还款和逾期订单
export interface CurrentRepayBill {
  current: boolean; // true 存在当期未还订单
  overDue: boolean; // true 存在逾期订单
}

export interface AutarkyStateType {
  /** 自营A老户升级状态(INIT、SUBMITTING、SUCCEEDED、FAILED ) */
  autarkyUpgradeState: UpgradeStateEnum;
  /** 是否被自营A圈选用户（true:被圈选 false:未被圈选） */
  autarky: boolean;
  /** 是否能新授信（包掉自营A以及自营B的逻辑），用于隐藏授信入口, true 表示可以授信 */
  canApplyCredit: boolean;
  /** 提现流程授信一体化 伽利略实验标识 */
  galileoType: galileoTypeEnum;
}

/** 海消联合品牌展示状态 */
export interface HaiXiaoQueryStatusType {
  /** 是否展示海消联合品牌入口（仅 scene=HOMEPAGE 时有意义 ，提现场景固定返回false） */
  showHaiXiaoBrand: boolean;
  /** 海消用户是否隐藏授信申请入口（首页+结果页均使用） */
  hideApplyCreditEntry: boolean;
}
